import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import socket from '../socket';
import { searchSongs, registerPublicLobby, updatePublicLobby, unregisterPublicLobby } from '../api';
import SongCard from '../components/SongCard';

function Lobby() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const [lobby, setLobby] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [mySongs, setMySongs] = useState([]);
  const [countdown, setCountdown] = useState(null);

  const isHost = lobby?.host === socket.id;

  useEffect(() => {
    socket.on('lobby-updated', (updatedLobby) => {
      setLobby(updatedLobby);
    });

    socket.on('tournament-started', () => {
      navigate(`/battle/${roomCode}`);
    });

    socket.on('kicked', () => {
      navigate('/', { replace: true });
    });

    socket.on('countdown-start', ({ seconds }) => {
      setCountdown(seconds);
      let count = seconds;
      const timer = setInterval(() => {
        count--;
        if (count <= 0) {
          clearInterval(timer);
          setCountdown(null);
        } else {
          setCountdown(count);
        }
      }, 1000);
    });

    // If we're already connected (navigated from Join page), request lobby state
    if (socket.connected) {
      socket.emit('get-lobby', { roomCode }, (response) => {
        if (response.success) {
          setLobby(response.lobby);
        }
      });
      socket.emit('get-my-songs', { roomCode }, (response) => {
        if (response.success) setMySongs(response.songs);
      });
    }

    // Also handle the connected event for initial lobby data
    socket.on('connected', (data) => {
      if (data.lobby) setLobby(data.lobby);
    });

    return () => {
      socket.off('lobby-updated');
      socket.off('tournament-started');
      socket.off('kicked');
      socket.off('countdown-start');
      socket.off('connected');
    };
  }, [roomCode, navigate]);

  // Keep public lobby registry in sync
  useEffect(() => {
    if (lobby?.isPublic && isHost) {
      updatePublicLobby({
        roomCode,
        playerCount: lobby.players.length,
        songCount: lobby.songCount,
        songsSubmitted: lobby.songsSubmitted
      });
    }
  }, [lobby?.players?.length, lobby?.songsSubmitted, lobby?.isPublic, isHost, roomCode, lobby?.songCount]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const data = await searchSongs(searchQuery);
      setSearchResults(data.tracks);
    } catch (err) {
      setError('Suche fehlgeschlagen');
    }
    setSearching(false);
  }, [searchQuery]);

  const handleSubmitSong = (song) => {
    socket.emit('submit-song', { roomCode, song }, (response) => {
      if (!response.success) {
        setError(response.error);
        setTimeout(() => setError(''), 3000);
      } else {
        setSearchResults([]);
        setSearchQuery('');
        setError('');
        // Refresh my songs
        socket.emit('get-my-songs', { roomCode }, (res) => {
          if (res.success) setMySongs(res.songs);
        });
      }
    });
  };

  const handleRemoveSong = (songId) => {
    socket.emit('remove-song', { roomCode, songId }, (response) => {
      if (response.success) {
        setMySongs(prev => prev.filter(s => s.id !== songId));
      }
    });
  };

  const handleSetSongCount = (count) => {
    socket.emit('set-song-count', { roomCode, songCount: count }, () => {});
  };

  const handleSetVotingDuration = (duration) => {
    socket.emit('set-voting-duration', { roomCode, duration }, () => {});
  };

  const handleSetMaxSongsPerPlayer = (max) => {
    socket.emit('set-max-songs-per-player', { roomCode, max }, () => {});
  };

  const handleTogglePresentationMode = () => {
    socket.emit('set-presentation-mode', { roomCode, enabled: !lobby.presentationMode }, () => {});
  };

  const handleToggleShowSubmitter = () => {
    socket.emit('set-show-submitter', { roomCode, enabled: !lobby.showSubmitter }, () => {});
  };

  const handleKickPlayer = (playerId) => {
    socket.emit('kick-player', { roomCode, playerId }, (response) => {
      if (!response.success) {
        setError(response.error);
        setTimeout(() => setError(''), 3000);
      }
    });
  };

  const handleTogglePublic = () => {
    const newPublic = !lobby.isPublic;
    socket.emit('set-public', { roomCode, isPublic: newPublic }, (response) => {
      if (response.success) {
        if (newPublic) {
          const hostPlayer = lobby.players.find(p => p.isHost);
          registerPublicLobby({
            roomCode,
            hostName: hostPlayer?.name || 'Host',
            playerCount: lobby.players.length,
            songCount: lobby.songCount,
            songsSubmitted: lobby.songsSubmitted
          });
        } else {
          unregisterPublicLobby(roomCode);
        }
      }
    });
  };

  const handleStartTournament = () => {
    if (lobby?.isPublic) unregisterPublicLobby(roomCode);
    socket.emit('start-tournament', { roomCode }, (response) => {
      if (!response.success) {
        setError(response.error);
        setTimeout(() => setError(''), 3000);
      }
    });
  };

  const inviteLink = `${window.location.origin}/join/${roomCode}`;
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!lobby) {
    return <div className="loading-page"><div className="spinner"></div></div>;
  }

  const poolFull = lobby.songsSubmitted >= lobby.songCount;
  const songsNeeded = lobby.songCount - lobby.songsSubmitted;

  if (countdown !== null) {
    return (
      <div className="countdown-overlay">
        <div className="countdown-content">
          <h1 className="countdown-number">{countdown}</h1>
          <p className="countdown-text">Turnier startet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lobby-page">
      <div className="lobby-header">
        <h1>Lobby</h1>
        <div className="room-info">
          <span className="room-code-display">{roomCode}</span>
        </div>
      </div>

      <div className="invite-section">
        <div className="invite-link-box">
          <input
            type="text"
            readOnly
            value={inviteLink}
            className="invite-link-input"
            onClick={(e) => e.target.select()}
          />
          <button className="btn btn-primary" onClick={copyLink}>
            {copied ? '✓' : '📋'}
          </button>
        </div>
        <div className="qr-code">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(inviteLink)}`}
            alt="QR Code"
            width="160"
            height="160"
          />
        </div>
      </div>

      <div className="lobby-content">
        <div className="lobby-sidebar">
          <div className="players-section">
            <h3>Spieler ({lobby.players.length})</h3>
            <ul className="player-list">
              {lobby.players.map((player) => (
                <li key={player.id} className={player.isHost ? 'host' : ''}>
                  <span className="player-name-row">
                    {player.name}
                    {player.id === socket.id && <span className="you-badge">Du</span>}
                    {player.isHost && <span className="host-badge">Host</span>}
                  </span>
                  {isHost && !player.isHost && (
                    <button
                      className="btn-kick"
                      onClick={() => handleKickPlayer(player.id)}
                      title="Spieler kicken"
                    >
                      ✕
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Host settings toggle */}
          {isHost && (
            <button
              className="btn btn-ghost settings-toggle"
              onClick={() => setShowSettings(!showSettings)}
            >
              ⚙️ Einstellungen {showSettings ? '▲' : '▼'}
            </button>
          )}

          {/* Host settings panel */}
          {isHost && showSettings && (
            <div className="settings-panel">
              <div className="settings-section">
                <h4>Turnier-Größe</h4>
                <div className="song-count-options">
                  {[8, 16, 32, 64].map((count) => (
                    <button
                      key={count}
                      className={`btn song-count-btn ${lobby.songCount === count ? 'active' : ''}`}
                      onClick={() => handleSetSongCount(count)}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>

              <div className="settings-section">
                <h4>Voting-Dauer</h4>
                <div className="song-count-options">
                  {[15, 20, 30, 45, 60, 0].map((sec) => (
                    <button
                      key={sec}
                      className={`btn song-count-btn ${lobby.votingDuration === sec ? 'active' : ''}`}
                      onClick={() => handleSetVotingDuration(sec)}
                    >
                      {sec === 0 ? '∞' : `${sec}s`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="settings-section">
                <h4>Max Songs pro Spieler</h4>
                <div className="song-count-options">
                  {[0, 2, 4, 6, 8].map((max) => (
                    <button
                      key={max}
                      className={`btn song-count-btn ${lobby.maxSongsPerPlayer === max ? 'active' : ''}`}
                      onClick={() => handleSetMaxSongsPerPlayer(max)}
                    >
                      {max === 0 ? '∞' : max}
                    </button>
                  ))}
                </div>
              </div>

              <div className="settings-section">
                <h4>Lobby Sichtbarkeit</h4>
                <button
                  className={`btn ${lobby.isPublic ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={handleTogglePublic}
                  style={{ width: '100%' }}
                >
                  {lobby.isPublic ? '🌐 Öffentlich' : '🔒 Privat'}
                </button>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
                  {lobby.isPublic ? 'Lobby ist auf der Startseite sichtbar' : 'Nur über Einladungslink erreichbar'}
                </p>
              </div>

              <div className="settings-section">
                <h4>Präsentationsmodus</h4>
                <button
                  className={`btn ${lobby.presentationMode ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={handleTogglePresentationMode}
                  style={{ width: '100%' }}
                >
                  {lobby.presentationMode ? '📺 An' : '○ Aus'}
                </button>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
                  {lobby.presentationMode
                    ? 'Host stimmt nicht ab, sondern präsentiert auf einem großen Monitor und sieht Votes live'
                    : 'Host stimmt normal mit ab'}
                </p>
              </div>

              <div className="settings-section">
                <h4>Einreicher sichtbar</h4>
                <button
                  className={`btn ${lobby.showSubmitter ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={handleToggleShowSubmitter}
                  style={{ width: '100%' }}
                >
                  {lobby.showSubmitter ? '👁️ Sichtbar' : '🙈 Versteckt'}
                </button>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
                  {lobby.showSubmitter
                    ? 'Während der Runde sieht man, wer den Song eingereicht hat'
                    : 'Einreicher bleibt geheim – nur beim Gewinner-Song wird der Name gezeigt'}
                </p>
              </div>
            </div>
          )}

          {/* Progress info */}
          <div className="pool-progress">
            <div className="progress-info">
              <span className="progress-label">Songs</span>
              <span className="progress-count">{lobby.songsSubmitted}/{lobby.songCount}</span>
            </div>
            <div className="progress-bar-bg">
              <div
                className="progress-bar-fill"
                style={{ width: `${Math.min(100, (lobby.songsSubmitted / lobby.songCount) * 100)}%` }}
              />
            </div>
            <p className="progress-hint">
              ~{lobby.songsPerPlayer} Songs pro Spieler
              {songsNeeded > 0 && ` • noch ${songsNeeded} nötig`}
              {poolFull && ' • Pool voll!'}
            </p>
          </div>

          {isHost && (
            <button
              className="btn btn-primary btn-large start-btn"
              onClick={handleStartTournament}
              disabled={lobby.songsSubmitted < 2}
            >
              🏆 Turnier starten
            </button>
          )}
        </div>

        <div className="lobby-main">
          <div className="search-section">
            <h3>Song hinzufügen</h3>
            <div className="search-bar">
              <input
                type="text"
                placeholder="Song suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn btn-primary" onClick={handleSearch} disabled={searching || poolFull}>
                {searching ? '...' : '🔍'}
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((track) => (
                  <SongCard
                    key={track.id}
                    song={track}
                    action={poolFull ? null : 'add'}
                    onAction={() => handleSubmitSong(track)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* My submitted songs */}
          {mySongs.length > 0 && (
            <div className="my-songs-section">
              <h3>Deine Songs ({mySongs.length})</h3>
              <div className="my-songs-list">
                {mySongs.map((song) => (
                  <SongCard
                    key={song.id}
                    song={song}
                    action="remove"
                    onAction={() => handleRemoveSong(song.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Lobby;
